import { I18n } from "@assets/strings";
import React, { JSX, useEffect, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Group } from "@domain/group/group";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Row } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";

import { useConfirmation } from "@ui/modals/useConfirmation";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";

import moment from "moment";
import smallUpcomingImage from "@assets/images/live/small_upcoming.png";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useWorkspaces } from "@domain/group/workspace/hooks";
import { useParticipants } from "@domain/livecourse/hooks";
import { Livecourse, LivecourseType } from "@domain/livecourse/livecourse";
import { useGroupMembers, useUserProfile } from "@domain/user/hooks";
import { OnGoingLiveCourseIcon } from "@ui/components/animatedIcons/onGoingLiveCourseIcon";
import { DefaultButton } from "@ui/components/buttons/defaultButton";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { CalendarIcon, TimeIcon } from "@ui/components/icons";
import { LiveMembers } from "@ui/components/profile/community/liveMembers";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { colors, liveColors } from "@ui/styles/colors";
import { LivecourseWorkspaceLabel } from "./livecourseWorkspaceLabel";

interface GroupLivecoursesThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
	livecourse: Livecourse;
}

interface OrganizationLivecoursesThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	organizationId: string;
	livecourse: Livecourse;
	isAdmin: boolean;
}

export const GroupLivecoursesThumbnail: React.FC<GroupLivecoursesThumbnailProps> = ({
	livecourse,
	shareroom,
	...htmlProps
}) => {
	const { livecourseService } = useServices();
	const [joined, setJoined] = useState(false);
	const currentProfile = useCurrentProfile();
	const { result: members, totalElements, fetch } = useParticipants(livecourse.id);
	const { result: groupMembers } = useGroupMembers(shareroom.id, "ADMINISTRATOR");
	const [isCreator, setIsCreator] = useState<boolean>(false);

	const askConfirmation = useConfirmation(I18n.t("live.deleteconfirm"), I18n.t("delete"), "");
	const history = useHistory();
	const { result: profile } = useUserProfile(`${livecourse.organizer}`);
	const { result: shareroomworkspaces } = useWorkspaces(shareroom.id);
	const theWorkspace = shareroomworkspaces?.find((c) => c?.id === livecourse.workspaceId) || shareroomworkspaces[0];
	const starthour = moment(livecourse.startDate).format("hh:mma");
	const endhour = moment(livecourse.endDate).format("hh:mma");

	const type: LivecourseType = livecourse.type;
	useEffect(() => {
		{
			const joined = !!currentProfile && members.map((mb) => mb.id).includes(currentProfile.id);

			setJoined(joined);
			setIsCreator(
				!!(
					currentProfile &&
					groupMembers &&
					(currentProfile.id === livecourse.organizer || !!groupMembers.find((m) => m.id === currentProfile.id))
				)
			);
		}
	}, [currentProfile, members, groupMembers]);

	const joinButton = (): JSX.Element => {
		return !isCreator ? (
			joined && type === LivecourseType.INSTANT ? (
				<JoinButton>
					<PrimaryButton
						onClick={async () => {
							global.window.location.href = livecourse.participationLink;
						}}
					>
						{I18n.t("live.join")}
					</PrimaryButton>
				</JoinButton>
			) : joined && type === LivecourseType.SCHEDULED ? (
				<AlreadyJoinButton>{I18n.t("live.confirmed")}</AlreadyJoinButton>
			) : !joined ? (
				<JoinButton>
					<PrimaryButton
						onClick={async () => {
							try {
								const { participationLink } = await livecourseService.confirm(livecourse.id);
								createToast(I18n.t("success"), I18n.t("live.confirmed"), "success");
								setJoined(true);
								fetch();
								if (type === LivecourseType.INSTANT && participationLink)
									global.window.location.href = participationLink;
							} catch {
								createToast(I18n.t("errormessage"), I18n.t("live.declined"), "error");
							}
						}}
					>
						{type === LivecourseType.INSTANT ? I18n.t("live.join") : I18n.t("live.joininstantroom")}
					</PrimaryButton>
				</JoinButton>
			) : (
				<></>
			)
		) : (
			<JoinButton>
				<PrimaryButton
					onClick={async () => {
						const { participationLink } = await livecourseService.confirm(livecourse.id);
						if (participationLink) global.window.location.href = participationLink;
						else createToast(I18n.t("errormessage"), "error", "error");
					}}
				>
					{I18n.t("live.joinascreator")}
				</PrimaryButton>
			</JoinButton>
		);
	};

	return (
		<Container style={{ minHeight: 389 }} {...htmlProps}>
			<TopSide background={liveColors[0]}>
				<Row style={{ marginBottom: -11 }}>
					{type === LivecourseType.INSTANT ? (
						<Bloc>
							<OnGoingLiveCourseIcon />
						</Bloc>
					) : (
						<img style={{ marginTop: 11 }} src={smallUpcomingImage} width={34} height={20} />
					)}
					{type === LivecourseType.INSTANT ? (
						<PrimaryTitle style={{ paddingLeft: 14 }}> {I18n.t("live.ongoing")}</PrimaryTitle>
					) : (
						<SecondaryTitle style={{ paddingLeft: 9 }}> {I18n.t("live.upcoming")} </SecondaryTitle>
					)}
				</Row>
				<LivecoursesTitle>{livecourse.title}</LivecoursesTitle>
				<DateContent>
					<Row gap={10} align="center">
						{type === LivecourseType.INSTANT ? <RedVerticalBox /> : <BlueVerticalBox />}
						<Row align="center">
							{type === LivecourseType.INSTANT ? (
								<Row gap={0} align="center">
									<DateElementTitle>
										{I18n.t("live.startedat")} {starthour}
									</DateElementTitle>
								</Row>
							) : (
								<Row gap={10} align="center">
									<Row align="center">
										<TimeIcon color="black" />
										<DateElementTitle style={{ paddingLeft: 5 }}>
											{starthour} à {endhour}
										</DateElementTitle>
									</Row>
									<Row align="center">
										<CalendarIcon width={18} height={18} />
										<DateElementTitle>{moment(livecourse.startDate).format("DD/MM/YY")}</DateElementTitle>
									</Row>
								</Row>
							)}
						</Row>
					</Row>
				</DateContent>
				<Bloc style={{ marginBottom: 6 }}>{I18n.t("live.createdby")}</Bloc>
				<Bloc>
					<Row gap={5} align="center">
						<MemberPicture spacing={5} picture={profile?.avatar} size={"S"} onClick={() => ({})} />
						<AdminMember>{profile?.primaryName}</AdminMember>
					</Row>
				</Bloc>

				<Row style={{ marginBottom: 6 }}>
					<Bloc style={{ marginBottom: 6 }}>
						<StyledWorkspaceLabel workspace={theWorkspace?.name} small={true} />
					</Bloc>
				</Row>

				<Bloc style={{ minHeight: 55 }}>
					{I18n.t("live.members")} ({totalElements})
					<LiveMembers sample={members} count={members.length} spacing={-8} />
				</Bloc>
				{joinButton()}
			</TopSide>
			{shareroom.userRole === "ADMINISTRATOR" && (
				<StyledMenu blur>
					<SimpleMenuContent
						onEdit={() =>
							type !== LivecourseType.INSTANT
								? history.push(Path.LiveGroupInfos(livecourse.id, shareroom.id))
								: undefined
						}
						onDelete={() =>
							askConfirmation(async () => {
								try {
									await livecourseService.delete(livecourse.id);

									createToast(I18n.t("success"), I18n.t("live.deletesuccess"), "success");
								} catch {
									createToast(I18n.t("error"), I18n.t("live.deletefail"), "error");
								}
							})
						}
					/>
				</StyledMenu>
			)}
		</Container>
	);
};

export const OrganizationLivecoursesThumbnail: React.FC<OrganizationLivecoursesThumbnailProps> = ({
	livecourse,
	organizationId,
	isAdmin = false,
	...htmlProps
}) => {
	const { livecourseService } = useServices();
	const [joined, setJoined] = useState(false);
	const currentProfile = useCurrentProfile();
	const { result: members, totalElements, fetch } = useParticipants(livecourse.id);
	const [isCreator, setIsCreator] = useState(currentProfile && currentProfile.id === livecourse.organizer);

	const askConfirmation = useConfirmation(I18n.t("live.deleteconfirm"), I18n.t("delete"), "");
	const history = useHistory();
	const { result: profile } = useUserProfile(`${livecourse.organizer}`);
	const starthour = moment.utc(livecourse.startDate).format("hh:mm");
	const endhour = moment.utc(livecourse.endDate).format("hh:mm");
	const type: LivecourseType = livecourse.type;
	useEffect(() => {
		{
			const joined = !!currentProfile && members.map((mb) => mb.id).includes(currentProfile.id);

			setJoined(joined);
			setIsCreator(currentProfile && currentProfile.id === livecourse.organizer);
		}
	}, [currentProfile, members]);

	const joinButton = (): JSX.Element => {
		return !isCreator ? (
			joined && type === LivecourseType.INSTANT ? (
				<JoinButton>
					<PrimaryButton
						onClick={async () => {
							global.window.location.href = livecourse.participationLink;
						}}
					>
						{I18n.t("live.join")}
					</PrimaryButton>
				</JoinButton>
			) : joined && type === LivecourseType.SCHEDULED ? (
				<AlreadyJoinButton>{I18n.t("live.confirmed")}</AlreadyJoinButton>
			) : joined && type === LivecourseType.OVER ? (
				<AlreadyJoinButton style={{ backgroundColor: "grey" }}>{"terminé"}</AlreadyJoinButton>
			) : !joined ? (
				<JoinButton>
					<PrimaryButton
						onClick={async () => {
							try {
								const { participationLink } = await livecourseService.confirm(livecourse.id);
								createToast(I18n.t("success"), I18n.t("live.confirmed"), "success");
								setJoined(true);
								fetch();
								if (type === LivecourseType.INSTANT && participationLink)
									global.window.location.href = participationLink;
							} catch {
								createToast(I18n.t("errormessage"), I18n.t("live.declined"), "error");
							}
						}}
					>
						{type === LivecourseType.INSTANT ? I18n.t("live.join") : I18n.t("live.joininstantroom")}
					</PrimaryButton>
				</JoinButton>
			) : (
				<></>
			)
		) : (
			<JoinButton>
				<PrimaryButton
					onClick={async () => {
						const { participationLink } = await livecourseService.confirm(livecourse.id);
						if (participationLink) global.window.location.href = participationLink;
						else createToast(I18n.t("errormessage"), "error", "error");
					}}
				>
					{I18n.t("live.joinascreator")}
				</PrimaryButton>
			</JoinButton>
		);
	};

	return (
		<ContainerOrg {...htmlProps}>
			<TopSide
				// onClick={() => {
				// 	history.push(Path.LiveOrganizationInfos(livecourse.id, organizationId));
				// }}
				background={liveColors[0]}
			>
				<Row style={{ marginBottom: 6 }}>
					{type === LivecourseType.INSTANT ? (
						<Bloc>
							<OnGoingLiveCourseIcon />
						</Bloc>
					) : (
						<img src={smallUpcomingImage} width={34} height={20} />
					)}
					{type === LivecourseType.INSTANT ? (
						<PrimaryTitle style={{ paddingLeft: 3 }}> {I18n.t("live.ongoing")}</PrimaryTitle>
					) : (
						<SecondaryTitle style={{ paddingLeft: 3 }}> {I18n.t("live.upcoming")} </SecondaryTitle>
					)}
				</Row>
				<LivecoursesTitle style={{ minHeight: 47 }}>{livecourse.title}</LivecoursesTitle>
				<DateContent style={{ marginTop: 0 }}>
					<Row gap={10} align="center">
						{type === LivecourseType.INSTANT ? <RedVerticalBox /> : <BlueVerticalBox />}
						<Row align="center">
							{type === LivecourseType.INSTANT ? (
								<Row gap={0} align="center">
									<DateElementTitle>
										{I18n.t("live.startedat")} {starthour}
									</DateElementTitle>
								</Row>
							) : (
								<Row gap={10} align="center">
									<Row align="center">
										<TimeIcon color={"darkgray"} />
										<DateElementTitle>
											{starthour} à {endhour}
										</DateElementTitle>
									</Row>
									<Row gap={10} align="center">
										<CalendarIcon width={20} height={20} />
										<DateElementTitle>{moment(livecourse.startDate).format("DD/MM/YY")}</DateElementTitle>
									</Row>
								</Row>
							)}
						</Row>
					</Row>
				</DateContent>
				<Bloc style={{ marginBottom: 6 }}>{I18n.t("live.createdby")}</Bloc>
				<Bloc>
					<Row gap={5} align="center">
						<MemberPicture spacing={5} picture={profile?.avatar} onClick={() => ({})} />
						<AdminMember>{profile?.primaryName}</AdminMember>
					</Row>
				</Bloc>
				<Bloc style={{ minHeight: 55 }}>
					{I18n.t("live.members")} ({totalElements})
					<LiveMembers sample={members} count={members.length} spacing={-8} />
				</Bloc>
				{joinButton()}
			</TopSide>
			{type === LivecourseType.INSTANT}
			{isAdmin && (
				<StyledMenu blur>
					<SimpleMenuContent
						onEdit={
							type === LivecourseType.SCHEDULED
								? () =>
										history.push(Path.LiveOrganizationEditor(organizationId, LivecourseType.SCHEDULED, livecourse.id))
								: undefined
						}
						onDelete={() =>
							askConfirmation(async () => {
								try {
									await livecourseService.delete(livecourse.id);
									createToast(I18n.t("success"), I18n.t("live.deletesuccess"), "success");
								} catch {
									createToast(I18n.t("error"), I18n.t("live.deletefail"), "error");
								}
							})
						}
					/>
				</StyledMenu>
			)}
		</ContainerOrg>
	);
};
const MemberPicture = styled(ProfilePicture)<{ first?: boolean; spacing: number }>`
	cursor: pointer;
	margin-left: ${({ first, spacing }) => (first ? 0 : spacing)}px;
	background-color: ${(props) => props.theme.colors.backgroundPrimary};
`;

const Container = styled(RoundedBlock)`
	position: relative;
	overflow: hidden;
	height: 389px;
	> a  {
		display: block;
		height: 100%;
		> div  {
			flex: 1;
		}
	}
`;

const ContainerOrg = styled(RoundedBlock)`
	position: relative;
	overflow: hidden;
	height: 347px;
	> a  {
		display: block;
		height: 100%;
		> div  {
			flex: 1;
		}
	}
	@media (min-width: 600px) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (min-width: 900px) {
		grid-template-columns: repeat(3, 1fr);
	}
`;

const TopSide = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "background",
})<{ background: string }>`
	position: relative;
	overflow: hidden;
	background-color: ${({ background }) => background};
	padding: 20px 80px 30px 20px;
	${({ theme }) => theme.textStyles.subtitle};
	cursor: pointer;
`;
const Bloc = styled.div`
	position: relative;
	font-family: Sora;
	margin-top: 12px;
	${({ theme }) => theme.colors.primary};
	font-size: 13px;
	font-weight: 100;
`;
const PrimaryTitle = styled(Bloc)`
	color: ${colors.primary};
	font-size: 0.9375rem;
`;
const SecondaryTitle = styled(Bloc)`
	color: ${colors.secondary};
	font-size: 0.9375rem;
`;

const LivecoursesTitle = styled.div`
	position: relative;
	margin-top: 20px;
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
	${({ theme }) => theme.textStyles.primaryBold};
`;
const DateElementTitle = styled.div`
	position: relative;
	font-family: Sora;
	color: #4b4b4b;
	font-size: 12px;
	font-weight: 200;
`;

const JoinButton = styled.div`
	position: relative;
	font-family: Sora;
	margin-top: 12px;
	${({ theme }) => theme.colors.primary};
	font-size: 13px;
	font-weight: 100;
	justify-content: center;
	align-items: center;
`;

export const AlreadyJoinButton = styled(DefaultButton)`
	position: relative;
	margin-top: 10px;
	font-family: Sora;
	background-color: #10c374;
	color: white;
	width: 270px;
	cursor: not-allowed;
	:hover  {
		background-color: #10c374;
	}
`;

const AdminMember = styled.div`
	position: relative;
	font-family: Sora;
	color: #737373;
	font-size: 13px;
	font-weight: 100;
`;

const StyledMenu = styled(KebabMenu)`
	position: absolute;
	right: 10px;
	top: 14px;
	background-color: ${({ theme }) => theme.colors.simplegray};
`;

const DateContent = styled(RoundedBlock)`
	display: flex;
	flex: 1;
	flex-direction: row;
	align-items: center;
	${gap(20)};
	border-color: "red";
	margin-top: 20px;
	background-color: ${({ theme }) => theme.colors.simplegray};
	height: 41px;
	width: 285px;
	margin-right: 5px;
	border-radius: 7px;
`;

const RedVerticalBox = styled.div`
	position: relative;
	left: 3;
	top: 0;
	height: 37px;
	width: 10px;
	background: #e83636;
	border: 1px solid #f5f3f3;
	box-sizing: border-box;
	border-radius: 87px;
`;

const BlueVerticalBox = styled.div`
	position: relative;
	left: 3;
	top: 0;
	height: 37px;
	width: 10px;
	background: ${({ theme }) => theme.colors.secondary};
	border: 1px solid #f5f3f3;
	box-sizing: border-box;
	border-radius: 87px;
`;

const StyledWorkspaceLabel = styled(LivecourseWorkspaceLabel)<{ small?: boolean }>`
	//max-width: 180px;
	${({ small }) =>
		!small &&
		css`
			position: absolute;
			right: 5px;
		`}
`;
